import { Box, Button } from '@mui/material';
import React, { FormEvent, useState } from 'react';
import { useDispatch } from 'react-redux';

import { setAlert } from '../../features/slices/alertSlice';
import { changePassword } from '../../features/slices/authSlice';
import { AppDispatch } from '../../features/store';
import HCInput from '../Input';

interface ChangePasswordModalContentProps {
    onClose: () => void;
}

const ChangePasswordModalContent = ({
    onClose,
}: ChangePasswordModalContentProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!currentPassword.trim() || !newPassword.trim()) {
            dispatch(
                setAlert({
                    message: 'Please fill all password fields',
                    severity: 'error',
                }),
            );
            return;
        }
        if (newPassword !== confirmPassword) {
            dispatch(
                setAlert({
                    message: 'New password and confirmation do not match',
                    severity: 'error',
                }),
            );
            return;
        }
        if (newPassword.length < 6) {
            dispatch(
                setAlert({
                    message: 'New password must be at least 6 characters',
                    severity: 'error',
                }),
            );
            return;
        }
        if (currentPassword === newPassword) {
            dispatch(
                setAlert({
                    message: 'New password must be different from current password',
                    severity: 'error',
                }),
            );
            return;
        }
        setSubmitting(true);
        dispatch(changePassword({ currentPassword, newPassword }))
            .unwrap()
            .then((payload: { responseCode?: string; responseMessage?: string }) => {
                setSubmitting(false);
                if (payload?.responseCode === '200') {
                    dispatch(
                        setAlert({
                            message: 'Password changed successfully',
                            severity: 'success',
                        }),
                    );
                    onClose();
                } else {
                    dispatch(
                        setAlert({
                            message:
                                payload?.responseMessage ??
                                'Could not change password. Try again.',
                            severity: 'error',
                        }),
                    );
                }
            })
            .catch((err: unknown) => {
                setSubmitting(false);
                const message =
                    typeof err === 'string'
                        ? err
                        : (err as { message?: string })?.message;
                dispatch(
                    setAlert({
                        message:
                            message ??
                            'Could not change password. Try again later.',
                        severity: 'error',
                    }),
                );
            });
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box className="flex flex-col gap-2 py-2">
                <HCInput
                    label="Current password"
                    name="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                />
                <HCInput
                    label="New password"
                    name="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                />
                <HCInput
                    label="Confirm new password"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                />
                <Box className="flex justify-end gap-2 pt-2">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? 'Saving…' : 'Change password'}
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default ChangePasswordModalContent;
